import React, {
  useCallback, useEffect, useMemo, useReducer, useState,
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Drawer,
  Form,
  Row,
  Col,
  Select,
  notification,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { TaskHelpers } from 'ontraccr-common';
import useEntityOTProfileMap from '../../common/hooks/useEntityOTProfileMap';

import DrawerSubmitFooter from '../../common/containers/DrawerSubmitFooter';
import FormTextInput from '../../common/inputs/FormTextInput';
import DivisionSelector from '../../common/inputs/DivisionSelector';

import ManualEntryCustomFields from './ManualEntryCustomFields';
import ManualEntryTimeHoursInput from './ManualEntryTimeHoursInput';
import ManualEntryDatePicker from './ManualEntryDatePicker';
import Analytics from '../../helpers/Analytics';

import sortByString, { filterSelectDropdown, getIdMap, isNullOrUndefined } from '../../helpers/helpers';
import {
  dateValidator,
  getOpt,
  getCostcodeOptions,
  getPhaseOptions,
  getProjectOptions,
  getUserOptions,
  UNPHASED,
  convertFromTimeBasedToHourBased,
  ptoTimesValidator,
  createManualEntryTasks,
  timesAreBad,
  getTimesFromHoursMinutes,
  isNonZeroTimeEntryValid,
} from './manualEntryHelpers';
import {
  getErrorsFromResponses,
  prepareResponsePayload,
} from '../../forms/ResponderHelpers';
import { getPhaseCostcodeKey, getAllowedDivisionIds } from '../../forms/formHelpers';

import BorderlessButton from '../../common/buttons/BorderlessButton';
import { TIME_TYPE_FLAG } from '../../constants/Flags';
import Permissions from '../../auth/Permissions';
import OnTraccrSwitch from '../../common/inputs/OnTraccrSwitch';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import ManualEntryDateRangePicker from './ManualEntryDateRangePicker';
import { calculateHourBasedTimes } from '../../helpers/tasks';
import { FOOTER_HEIGHT } from '../../constants/Styles';

import useProjectOrGlobalCostcodes from '../../costcodes/useProjectOrGlobalCostcodes';
import CostcodesTreeSelector from '../../costcodes/CostcodesTreeSelector';

const DEFAULT_TYPES = [
  { label: 'Work', value: 'work' },
  { label: 'Break', value: 'break' },
];

const BASE_TIME_ENTRY_DATA = {
  totalTime: 0,
  workTime: 0,
  breakTime: 0,
};

function ManualEntryFormDrawer({
  visible,
  onClose,
  onSubmit,
  onManualProjectIdChange,
  selectedEntry,
  defaultSageShiftId,
  defaultClassId,
  defaultProjectId,
  defaultDate,
  defaultStartTime,
  defaultEndTime,
  fileMap,
  userId,
  userIds,
  entries = [],
  lockedDivisionId,
  divisionIds: formDivisionIds = [],
  user = {},
  allowInlineTimes,
  hideUserSelector,
  shouldRoundTimes = false,
  isTimeEntryTable = false,
}) {
  const { t } = useTranslation();

  const {
    projectId,
    phaseId,
    costcodeId,
    classId,
    type = 'work',
    id,
    sageShiftId,
    customData = [],
    divisionId,
    timezone = DateTime.local().zoneName,
    date: entryDate = defaultDate,
    startTime: selectedEntryStartTime = defaultStartTime ?? 0,
    endTime: selectedEntryEndTime = defaultEndTime,
    originalStart = defaultStartTime,
    originalEnd = defaultEndTime,
    hourBased: selectedEntryHourBased,
    userId: selectedEntryUserId,
    state: selectedEntryState = null,
    isNightShift: selectedEntryIsNightShift,
  } = selectedEntry || {};

  const ourClassId = classId || defaultClassId;
  const [form] = Form.useForm();

  const [currentDate, setCurrentDate] = useState(entryDate);

  const customFields = useSelector((state) => state.timecards.customFields);
  const sageShifts = useSelector((state) => state.sage.shifts);
  const userDivisions = useSelector((state) => state.users.userDivisions);
  const projects = useSelector((state) => state.projects.projects);
  const teams = useSelector((state) => state.teams.teams);
  const users = useSelector((state) => state.users.users);
  const currentUser = useSelector((state) => state.profile.profile);
  const userTeams = useSelector((state) => state.users.userTeams);
  const {
    paidFlags = [],
    settings: {
      autoBreakEnabled = false,
      manualEntryRoundingInterval = false,
      manualEntryRoundingSetting = 'automatic',
      enableZeroTimeEntries = true,
    } = {},
  } = useSelector((state) => state.settings.company);

  const paidFlagSet = new Set(paidFlags);

  const activePhases = useSelector((state) => state.costcodes.phases);
  const {
    unions = [],
    locals = [],
    classes = [],
  } = useSelector((state) => state.unions);
  const { settings = {} } = useSelector((state) => state.settings.company || {});

  const [currentDivisionId, setCurrentDivisionId] = useState(lockedDivisionId || divisionId);
  const [selectedUserIds, setSelectedUserIds] = useState(selectedEntryUserId ? [selectedEntryUserId] : []);
  const [otInitApplied, setOtInitApplied] = useState(false);

  const map = useEntityOTProfileMap({
    userIds: selectedUserIds,
    divisionIds: currentDivisionId ? [currentDivisionId] : [],
  });

  // Manual OT is enabled if all the selected users have manual OT enabled
  const enableManualOT = useMemo(() => (
    selectedUserIds.every((uId) => map[uId]?.enableManualOT)
  ), [map, selectedUserIds]);

  const {
    enableHourBasedTracking = false,
    reqProject: settingsReqProject,
    reqCostcode: settingsReqCostcode,
    enableLocalSelection,
    enableClassSelection,
    enableSageShiftSelection,
    useEndDateOvernight,
    showOriginalTimes,
    enableGlobalCCOnProject,
    enableNightShiftPremium,
  } = settings;

  const {
    hourBased: taskIsHourBased = (!defaultStartTime && !defaultEndTime) && !!enableHourBasedTracking,
  } = selectedEntry || {};

  const reqProject = isTimeEntryTable ? false : settingsReqProject;
  const reqCostcode = isTimeEntryTable ? false : settingsReqCostcode;

  const [hourBased, setHoursBased] = useState(enableHourBasedTracking && taskIsHourBased);
  const [selectedProject, setSelectedProject] = useState(projectId || defaultProjectId);
  const [selectedPhase, setSelectedPhase] = useState(projectId
    && phaseId === null
    && costcodeId
    ? UNPHASED
    : phaseId);
  const [selectedCostCode, setSelectedCostCode] = useState(costcodeId);
  const [selectedLocal, setSelectedLocal] = useState();
  const [customFieldErrors, setCustomFieldErrors] = useState({});
  const [timeData, setTimeData] = useState();
  const [showBreak, setShowBreak] = useState(false);
  const [showOvertime, setShowOvertime] = useState(false);
  const [showDoubleOvertime, setShowDoubleOvertime] = useState(false);
  const [showPTO, setShowPTO] = useState(false);
  const [currentType, setCurrentType] = useState(type);
  const [isMultiDayEntry, setIsMultiDayEntry] = useState(false);
  const [prevVisible, setPrevVisible] = useState(visible);

  const { costcodes: activeCostcodes } = useProjectOrGlobalCostcodes({
    projectId: selectedProject,
    divisionId: currentDivisionId,
    visible,
  });

  const [shouldUpdate, forceUpdate] = useReducer((x) => x + 1, 0);

  const hasPaidFlag = paidFlagSet.has(TIME_TYPE_FLAG);

  const {
    existingAllTasksTimeEntryData,
    existingSelectedEntryTimeEntryData,
  } = useMemo(() => {
    const data = { ...BASE_TIME_ENTRY_DATA };
    const selectedEntryData = { ...BASE_TIME_ENTRY_DATA };

    entries.forEach((task) => {
      const { type: entryType, id: taskId } = task;
      const runtimes = TaskHelpers.getRuntimes(task, true);
      const workRuntime = runtimes.regularTime + runtimes.overtime + runtimes.doubleOT;
      const breakRuntime = runtimes.breakTime;
      const totalRuntime = workRuntime + breakRuntime;

      const shouldAddToSelectedEntry = selectedEntry?.id === taskId;
      const isBreak = entryType === 'break';
      data.totalTime += totalRuntime;
      selectedEntryData.totalTime += shouldAddToSelectedEntry ? totalRuntime : 0;

      if (!isBreak) {
        data.workTime += workRuntime;
        selectedEntryData.workTime += shouldAddToSelectedEntry ? workRuntime : 0;
      } else {
        data.breakTime += breakRuntime;
        selectedEntryData.breakTime += shouldAddToSelectedEntry ? breakRuntime : 0;
      }
    });
    return {
      existingAllTasksTimeEntryData: data,
      existingSelectedEntryTimeEntryData: selectedEntryData,
    };
  }, [entries, selectedEntry]);

  useEffect(() => {
    if (visible) {
      return setTimeData(existingAllTasksTimeEntryData);
    }

    return setTimeData();
  }, [visible, existingAllTasksTimeEntryData]);

  const ourCustomFields = useMemo(() => {
    const {
      [currentDivisionId]: { fields = [] } = {},
    } = customFields;
    return fields;
  }, [customFields, currentDivisionId]);

  const isFutureDate = useMemo(() => {
    const currentDateTime = DateTime.fromSQL(currentDate).startOf('day');
    const now = DateTime.local();
    return currentDateTime > now;
  }, [currentDate]);

  const hasPTOPermission = Permissions.has('MANAGE_PTO');
  const hasFutureDayPermission = Permissions.has('MULTI_DAY_FUTURE_MANUAL_ENTRY');

  useEffect(() => {
    if (visible && isFutureDate && !hasFutureDayPermission && hasPaidFlag) {
      setShowBreak(false);
      setShowOvertime(false);
      setShowDoubleOvertime(false);
      form.setFieldsValue({
        startTime: null,
        endTime: null,
        breakStartTime: null,
        breakEndTime: null,
        otStartTime: null,
        otEndTime: null,
        doubleOTStartTime: null,
        doubleOTEndTime: null,
        ptoStartTime: null,
        ptoEndTime: null,
        hours: null,
        minutes: null,
        breakHours: null,
        breakMinutes: null,
        otHours: null,
        otMinutes: null,
        doubleOTHours: null,
        doubleOTMinutes: null,
        ptoHours: null,
        ptoMinutes: null,
      });
    }
  }, [visible, isFutureDate, hasFutureDayPermission, hasPaidFlag]);

  useEffect(() => {
    if (!enableManualOT) {
      setShowOvertime(false);
      setShowDoubleOvertime(false);
      form.setFieldsValue({
        otStartTime: null,
        otEndTime: null,
        doubleOTStartTime: null,
        doubleOTEndTime: null,
      });

      if (currentType === 'overtime') {
        form.setFieldsValue({
          type: 'work',
        });
        setCurrentType('work');
      }
    }
  }, [enableManualOT, currentType, form]);

  // Apply OT-dependent display values once the OT profile map has loaded for this entry.
  // The open-on-render block runs before the map is populated (map is {} on first render),
  // so we defer here.
  useEffect(() => {
    if (!visible || !selectedEntry) return;
    const entryUserId = selectedEntry.userId;
    if (entryUserId && !(entryUserId in map)) return;

    if (otInitApplied) return;
    setOtInitApplied(true);

    if (selectedEntry.hourBased) {
      form.setFieldsValue(calculateHourBasedTimes({ ...selectedEntry, enableManualOT }));
    } else if (!enableManualOT) {
      // Segments are sequential, so the span runs from the first segment start to the last end.
      // An all-OT entry has no regular segment, hence the fallbacks on both sides.
      const totalStartTime = TaskHelpers.getDisplayStart(selectedEntry, showOriginalTimes)
        || selectedEntry.otStartTime
        || selectedEntry.doubleOTStartTime
        || null;
      const totalEndTime = selectedEntry.doubleOTEndTime
        || selectedEntry.otEndTime
        || TaskHelpers.getDisplayEnd(selectedEntry, showOriginalTimes)
        || null;
      form.setFieldsValue({
        startTime: totalStartTime,
        endTime: totalEndTime,
        otStartTime: null,
        otEndTime: null,
        doubleOTStartTime: null,
        doubleOTEndTime: null,
      });
    }
  }, [map, visible, selectedEntry, enableManualOT, form, showOriginalTimes, otInitApplied]);

  // Uncomment this block when we enable PTO for paid flag off
  // useEffect(() => {
  //   if (!isFutureDate || !visible || hasPaidFlag || !hasPTOPermission) return;
  //   form.setFieldsValue({
  //     type: 'pto',
  //   });
  //   setCurrentType('pto');
  // }, [isFutureDate, visible, hasPaidFlag, hasPTOPermission]);

  const getComputedTimes = useCallback(({
    startTime,
    endTime,
    breakStartTime,
    breakEndTime,
    otStartTime,
    otEndTime,
    doubleOTStartTime,
    doubleOTEndTime,
    ptoStartTime,
    ptoEndTime,
    hours,
    breakHours,
    otHours,
    doubleOTHours,
    ptoHours,
    date: dateValue,
    dateRange,
  }) => {
    const date = dateRange?.length === 2 ? dateRange[0] : dateValue;
    let baseTimes = {
      startTime,
      endTime,
      breakStartTime,
      breakEndTime,
      otStartTime,
      otEndTime,
      doubleOTStartTime,
      doubleOTEndTime,
      ptoStartTime,
      ptoEndTime,
    };

    if (hourBased && date) {
      baseTimes = {
        ...getTimesFromHoursMinutes({
          date,
          timezone,
          hoursMinutes: hours,
          startTime,
          endTime,
          startTimeKey: 'startTime',
          endTimeKey: 'endTime',
          orderOffsetSource: selectedEntryStartTime,
        }),
        ...getTimesFromHoursMinutes({
          date,
          timezone,
          hoursMinutes: breakHours,
          startTime: breakStartTime,
          endTime: breakEndTime,
          startTimeKey: 'breakStartTime',
          endTimeKey: 'breakEndTime',
          orderOffsetSource: selectedEntryStartTime,
        }),
        ...getTimesFromHoursMinutes({
          date,
          timezone,
          hoursMinutes: otHours,
          startTime: otStartTime,
          endTime: otEndTime,
          startTimeKey: 'otStartTime',
          endTimeKey: 'otEndTime',
          orderOffsetSource: selectedEntryStartTime,
        }),
        ...getTimesFromHoursMinutes({
          date,
          timezone,
          hoursMinutes: doubleOTHours,
          startTime: doubleOTStartTime,
          endTime: doubleOTEndTime,
          startTimeKey: 'doubleOTStartTime',
          endTimeKey: 'doubleOTEndTime',
          orderOffsetSource: selectedEntryStartTime,
        }),
        ...getTimesFromHoursMinutes({
          date,
          timezone,
          hoursMinutes: ptoHours,
          startTime: ptoStartTime,
          endTime: ptoEndTime,
          startTimeKey: 'ptoStartTime',
          endTimeKey: 'ptoEndTime',
          orderOffsetSource: selectedEntryStartTime,
        }),
      };
    }

    return baseTimes;
  }, [hourBased, timezone, selectedEntryStartTime]);

  const getNewEntryTimeData = useCallback((data) => {
    const {
      startTime,
      endTime,
      breakStartTime,
      breakEndTime,
      otStartTime,
      otEndTime,
      doubleOTStartTime,
      doubleOTEndTime,
      ptoStartTime,
      ptoEndTime,
    } = getComputedTimes(data);

    const newType = data.type;

    if (
      (!newType || hasPaidFlag)
      || (
        !startTime
        && !endTime
        && !breakStartTime
        && !breakEndTime
        && !otStartTime
        && !otEndTime
        && !doubleOTStartTime
        && !doubleOTEndTime
        && !ptoStartTime
        && !ptoEndTime
      )) {
      return;
    }

    const newTimeData = {
      ...existingAllTasksTimeEntryData,
    };

    if (existingSelectedEntryTimeEntryData) {
      newTimeData.totalTime -= existingSelectedEntryTimeEntryData.totalTime;
      newTimeData.workTime -= existingSelectedEntryTimeEntryData.workTime;
      newTimeData.breakTime -= existingSelectedEntryTimeEntryData.breakTime;
    }

    const runtimes = TaskHelpers.getRuntimes({
      type: newType,
      startTime,
      endTime,
      breakStartTime,
      breakEndTime,
      otStartTime,
      otEndTime,
      doubleOTStartTime,
      doubleOTEndTime,
      ptoStartTime,
      ptoEndTime,
    }, true);
    const runtime = runtimes.regularTime + runtimes.overtime + runtimes.doubleOT;

    newTimeData.totalTime += runtime;

    if (newType === 'break') {
      newTimeData.breakTime += endTime - startTime;
    } else if (breakEndTime && breakStartTime) {
      newTimeData.breakTime += (breakEndTime - breakStartTime);
    } else {
      newTimeData.workTime += runtime;
    }

    setTimeData(newTimeData);
  }, [
    hourBased,
    selectedEntry,
    existingAllTasksTimeEntryData,
    existingSelectedEntryTimeEntryData,
    hasPaidFlag,
    getComputedTimes,
  ]);

  useEffect(() => {
    if (!visible || !selectedEntry) return;
    const {
      breakStartTime,
      breakEndTime,
      otStartTime,
      otEndTime,
      doubleOTStartTime,
      doubleOTEndTime,
      ptoStartTime,
      ptoEndTime,
    } = selectedEntry;

    if (breakStartTime && breakEndTime) setShowBreak(true);
    // Only surface OT/DT rows in manual OT mode; auto OT folds into regular/hours.
    if (enableManualOT && otStartTime && otEndTime) setShowOvertime(true);
    if (enableManualOT && doubleOTStartTime && doubleOTEndTime) setShowDoubleOvertime(true);
    if (ptoStartTime && ptoEndTime) setShowPTO(true);
  }, [visible, selectedEntry, enableManualOT]);

  const onValuesChange = useCallback((changedValues = {}, newValues = {}) => {
    if (isTimeEntryTable && Object.prototype.hasOwnProperty.call(changedValues, 'projectId')) {
      onManualProjectIdChange?.(changedValues.projectId ?? null);
    }

    const relevantTimeDataFields = new Set([
      'type',
      'startTime',
      'endTime',
      'breakStartTime',
      'breakEndTime',
      'otStartTime',
      'otEndTime',
      'doubleOTStartTime',
      'doubleOTEndTime',
      'hours',
      'breakHours',
      'otHours',
      'doubleOTHours',
      'ptoHours',
      'date',
    ]);
    const hasRelevantTimeData = Object.keys(changedValues).some(
      (key) => relevantTimeDataFields.has(key),
    );

    if (hasRelevantTimeData) {
      getNewEntryTimeData(newValues);
      forceUpdate();
    }

    const {
      projectId: selectedProjectId = null,
      phaseId: selectedPhaseId = null,
      localId: selectedLocalId,
      divisionId: newDivisionId,
      costcodeId: selectedCostCodeId,
      date: newDate,
      dateRange,
    } = newValues;
    setCurrentDate(isMultiDayEntry ? (dateRange?.[0] ?? newDate) : newDate);
    const formUpdate = { ...newValues };
    let newProjectId = selectedProjectId;
    if (newDivisionId && currentDivisionId !== newDivisionId) {
      newProjectId = null;
      formUpdate.projectId = null;
      formUpdate.sageShiftId = null;

      const filteredUsers = selectedUserIds.filter((userId) => (
        !!userDivisions?.[userId]?.find((divId) => divId === newDivisionId)
      ));
      setSelectedUserIds(filteredUsers);
    }

    setCurrentType(newValues.type);
    if (newValues.type === 'break') setShowBreak(false);
    if (newValues.type === 'overtime') setShowOvertime(false);
    if (newValues.type === 'pto') setShowPTO(false);

    setCurrentDivisionId(newDivisionId);
    setSelectedProject(newProjectId);
    setSelectedLocal(selectedLocalId);

    if (!selectedLocalId || selectedLocalId !== selectedLocal) {
      formUpdate.classId = null;
    }
    const relevantPhases = activePhases
      .filter((phase) => phase.projectId === newProjectId);
    if ((newProjectId ?? null) !== (selectedProject ?? null)) {
      let ourPhase = null;
      if (newProjectId) {
        if (relevantPhases.length === 0) {
          ourPhase = UNPHASED;
        }
      }

      formUpdate.phaseId = ourPhase;
      formUpdate.costcodeId = null;
      setSelectedPhase(ourPhase);
      setSelectedCostCode(null);
      form.setFieldsValue(formUpdate);
      return;
    }

    let newSelectedPhase = (
      selectedProjectId
      && selectedPhaseId === null
      && selectedCostCodeId
    )
      ? UNPHASED
      : selectedPhaseId;

    if (selectedProjectId && !newSelectedPhase && relevantPhases.length === 0) {
      newSelectedPhase = UNPHASED;
    }

    formUpdate.phaseId = newSelectedPhase;

    if ((newSelectedPhase ?? null) !== (selectedPhase ?? null)) {
      formUpdate.costcodeId = null;
      setSelectedPhase(newSelectedPhase);
      setSelectedCostCode(null);
    } else {
      setSelectedCostCode(selectedCostCodeId);
      setSelectedPhase(newSelectedPhase);
    }

    form.setFieldsValue(formUpdate);
  }, [
    form,
    selectedProject,
    selectedPhase,
    selectedLocal,
    currentDivisionId,
    getNewEntryTimeData,
    activePhases,
    activeCostcodes,
    userDivisions,
    isMultiDayEntry,
    selectedUserIds,
    isTimeEntryTable,
    onManualProjectIdChange,
  ]);

  const onSwitchChange = useCallback(() => {
    setHoursBased(!hourBased);
    if (!hourBased) {
      // Switching from time to hour based
      const values = form.getFieldsValue();
      form.setFieldsValue(convertFromTimeBasedToHourBased(values));
    } else {
      // Switching from hour based to time based
      // Multi day is only supported for hour based
      setIsMultiDayEntry(false);
      setShowPTO(false);
    }
  }, [hourBased, form]);

  const isOnlyPTO = useMemo(() => {
    const fieldValues = form.getFieldsValue();
    return (showPTO || (isFutureDate && hasPTOPermission && hasPaidFlag))
      && !fieldValues?.startTime
      && !fieldValues?.endTime
      && !fieldValues?.breakStartTime
      && !fieldValues?.breakEndTime
      && !fieldValues?.otStartTime
      && !fieldValues?.otEndTime
      && !fieldValues?.doubleOTStartTime
      && !fieldValues?.doubleOTEndTime
      && !fieldValues?.hours
      && !fieldValues?.breakHours
      && !fieldValues?.otHours
      && !fieldValues?.doubleOTHours;
  }, [shouldUpdate, form, showPTO, isFutureDate, hasPTOPermission, hasPaidFlag]);

  useEffect(() => {
    try {
      form.validateFields(['date']);
    } catch (err) {
      // fail silently
    }
  }, [
    form,
    isOnlyPTO,
  ]);

  const timecardPermissionMap = useMemo(() => {
    if (!customFields || !currentUser) return {};
    const permissionMap = {};
    const currentUserTeamIds = userTeams[currentUser.id] || [];

    ourCustomFields.forEach((section, sectionIndex) => {
      const permissions = customFields[currentDivisionId]?.fields[sectionIndex]?.settings?.permissions;
      if (!permissions) {
        permissionMap[section.id] = { canView: true, canEdit: true };
        return;
      }
      // Check User permission first
      if (permissions[currentUser.id]) {
        permissionMap[section.id] = { canView: permissions[currentUser.id]?.canView || false, canEdit: permissions[currentUser.id]?.canEdit || false };
        return;
      }
      // Check Role permission
      if (permissions[currentUser.positionId]) {
        permissionMap[section.id] = { canView: permissions[currentUser.positionId]?.canView || false, canEdit: permissions[currentUser.positionId]?.canEdit || false };
        return;
      }
      // Check Team permissions
      const teamPermission = currentUserTeamIds.find((teamId) => permissions[teamId]);
      if (teamPermission) {
        permissionMap[section.id] = { canView: permissions[teamPermission]?.canView || false, canEdit: permissions[teamPermission]?.canEdit || false };
        return;
      }
      permissionMap[section.id] = { canView: true, canEdit: true };
    });

    return permissionMap;
  }, [customFields, ourCustomFields, currentUser, divisionId, userTeams]);

  const onSubmitClicked = useCallback(async () => {
    let values;
    let responses = {};
    try {
      if (!hideUserSelector && (!selectedUserIds?.length)) {
        notification.error({ message: 'User is required' });
        return;
      }
      await form.validateFields(); // Throws if field error
      values = { ...form.getFieldsValue(), users: selectedUserIds };
      const {
        customData: formCustomData = {},
      } = values;
      responses = formCustomData;

      if (isTimeEntryTable) {
        setCustomFieldErrors({});
      } else {
        // check if there are required hidden fields that need to skip validation
        const {
          errorMap,
        } = getErrorsFromResponses({
          sections: ourCustomFields,
          responses,
          timecardPermissionMap,
          companySettings: settings,
        });
        setCustomFieldErrors(errorMap);
        if (!isOnlyPTO) {
          if (Object.keys(errorMap).length > 0) return;
        }
      }

      const computedTimes = getComputedTimes(values);
      values = {
        ...values,
        ...computedTimes,
      };

      if (hasPaidFlag) {
        const {
          badBreak,
          badTime,
          badOT,
          badDoubleOT,
          badPTO,
        } = timesAreBad({
          ...values,
          hourBased,
          showOvertime,
          showDoubleOvertime,
          showBreak,
          showPTO,
          currentType,
        });

        if (badBreak && badTime && badOT && badDoubleOT && badPTO) {
          notification.error({
            message: 'Please fill in the required time fields.',
          });
          return;
        }
      }

      if (!enableZeroTimeEntries) {
        const isValidNonZeroTimeEntry = isNonZeroTimeEntryValid({
          values,
          hourBased,
          currentType,
          isFutureDate,
          hasPTOPermission,
          showBreak,
          showOvertime,
          showDoubleOvertime,
          showPTO,
          shouldRoundTimes,
          manualEntryRoundingInterval,
          manualEntryRoundingSetting,
          timezone,
        });

        if (!isValidNonZeroTimeEntry) {
          notification.error({
            message: 'At least one non-zero time entry is required after rounding (regular time, overtime, double overtime, break, or PTO).',
          });
          return;
        }
      }
    } catch (err) {
      //
    }
    if (!values) return;
    const { responses: preparedResponses } = prepareResponsePayload({
      sections: ourCustomFields,
      responses,
    });

    const tasks = createManualEntryTasks({
      values,
      timezone,
      hourBased,
      currentType,
      selectedEntryHourBased,
      entryDate,
      selectedEntryStartTime,
      selectedEntryEndTime,
      shouldRoundTimes,
      manualEntryRoundingInterval,
      manualEntryRoundingSetting,
      preparedResponses,
      hasPaidFlag,
      showOvertime,
      showDoubleOvertime,
      showBreak,
      showPTO,
      isFutureDate,
      useEndDateOvernight,
      originalStart,
      originalEnd,
      showOriginalTimes,
      isMultiDayEntry,
    });

    Analytics.track('Manual Entry Done', {
      baseTaskType: values.type,
      isHourBased: hourBased,
      isUsingInlineBreak: showBreak,
      isUsingInlineOvertime: showOvertime,
    });

    // If we are in the time entry table, we only want to submit the first task as multi day is not supported
    const result = isTimeEntryTable ? tasks?.[0] : tasks;
    await onSubmit(result, hourBased);
  }, [
    form,
    onSubmit,
    id,
    hourBased,
    ourCustomFields,
    showBreak,
    showOvertime,
    showDoubleOvertime,
    showPTO,
    timezone,
    selectedEntryStartTime,
    selectedEntryHourBased,
    originalStart,
    originalEnd,
    showOriginalTimes,
    entryDate,
    hasPaidFlag,
    useEndDateOvernight,
    selectedEntry,
    isOnlyPTO,
    isFutureDate,
    isTimeEntryTable,
    isMultiDayEntry,
    settings,
    currentType,
    getComputedTimes,
    enableZeroTimeEntries,
    hasPTOPermission,
    hideUserSelector,
    selectedUserIds,
  ]);

  const activeProjects = useMemo(() => (
    projects.filter((project) => project.active)
  ), [projects]);
  const userDivs = useMemo(() => userDivisions[userId] ?? [], [userDivisions, userId]);
  const relevantDivisions = useMemo(() => userId ? userDivs : (formDivisionIds || []), [userId, userDivs, formDivisionIds]);
  const projectMap = useMemo(() => getIdMap(activeProjects), [activeProjects]);
  const ccMap = useMemo(() => getIdMap(activeCostcodes), [activeCostcodes]);

  const availableDivisionIds = useMemo(() => new Set(
    getAllowedDivisionIds({
      userDivisions,
      userId,
      formDivisionIds,
    }),
  ), [userDivisions, userId, formDivisionIds]);

  const projectOptions = useMemo(() => getProjectOptions({
    activeProjects,
    teams,
    user,
    divisionId: currentDivisionId,
    projectId: selectedProject,
    settings,
  }), [activeProjects, user, selectedProject, currentDivisionId, settings]);

  const phaseOptions = useMemo(() => {
    const opts = getPhaseOptions({
      activeCostcodes,
      activePhases,
      projectId: selectedProject,
    });

    if (
      enableGlobalCCOnProject
      && selectedProject
      && isTimeEntryTable
      && !opts.find((opt) => opt.value === UNPHASED)
    ) {
      opts.unshift({
        label: UNPHASED,
        value: UNPHASED,
      });
    }
    return opts;
  }, [activeCostcodes, activePhases, selectedProject, enableGlobalCCOnProject, isTimeEntryTable]);

  const costcodeOptions = useMemo(() => getCostcodeOptions({
    activeCostcodes,
    activePhases,
    projectId: selectedProject,
    phaseId: selectedPhase,
    divisionId: currentDivisionId,
  }), [activeCostcodes, activePhases, selectedProject, selectedPhase, currentDivisionId]);

  const userOptions = useMemo(() => getUserOptions({
    user,
    users,
    divisionId: currentDivisionId,
    userDivisions,
  }), [user, users, userDivisions, currentDivisionId, user]);

  const onlyUnphased = phaseOptions.length === 1 && phaseOptions[0].value === UNPHASED;

  const localMap = useMemo(() => getIdMap(locals), [locals]);
  const classMap = useMemo(() => getIdMap(classes), [classes]);
  const defaultLocalId = useMemo(() => {
    const {
      [ourClassId]: { localId: ourLocalId } = {},
    } = classMap;
    return ourLocalId;
  }, [ourClassId, classMap]);
  const relevantLocals = useMemo(() => {
    const {
      [selectedLocal || defaultLocalId]: { unionId: ourUnionId } = {},
    } = localMap;
    return locals.filter((local) => local.unionId === ourUnionId)
      .map((local) => getOpt(local));
  }, [selectedLocal, localMap, locals, defaultLocalId]);

  const relevantClasses = useMemo(() => (
    classes.filter((unionClass) => unionClass.localId === selectedLocal)
      .map((unionClass) => getOpt(unionClass))
  ), [classes, selectedLocal]);

  const sageShiftArray = useMemo(() => {
    const shiftArr = Object
      .values(sageShifts)
      .filter((shift) => shift.divisionId === currentDivisionId);
    shiftArr.sort(sortByString('name'));
    return shiftArr.map((shift) => getOpt(shift));
  }, [sageShifts, currentDivisionId]);

  const typeOptions = useMemo(() => {
    const types = [];

    const allowDefaultTypes = !isFutureDate || hasFutureDayPermission;
    if (allowDefaultTypes) types.push(...DEFAULT_TYPES);

    // if (Permissions.has('MANAGE_PTO')) {
    //   types.push({ label: 'PTO', value: 'pto' });
    // }

    if (allowDefaultTypes && enableManualOT) {
      types.push({ label: 'Overtime', value: 'overtime' });
    }

    if (allowDefaultTypes && type === 'service') {
      types.push({ label: 'Service', value: 'service' });
    }

    return types;
  }, [enableManualOT, type, isFutureDate, hasFutureDayPermission]);

  const shiftedEndTime = useMemo(() => {
    if (!selectedEntryHourBased || !selectedEntryStartTime) {
      return (
        TaskHelpers.getDisplayEnd({ endTime: selectedEntryEndTime, originalEnd }, showOriginalTimes)
      );
    }

    const startValue = DateTime.fromMillis(selectedEntryStartTime, { zone: timezone });
    const startTimeMs = (startValue.minute * 60000);
    return selectedEntryEndTime - startTimeMs;
  }, [
    selectedEntryHourBased,
    selectedEntryEndTime,
    selectedEntryStartTime,
    timezone,
    showOriginalTimes,
    originalEnd,
  ]);

  useEffect(() => {
    setSelectedLocal(defaultLocalId);
  }, [defaultLocalId]);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      form.setFieldsValue({
        divisionId: lockedDivisionId,
        projectId: defaultProjectId ?? null,
        phaseId: null,
        costcodeId: null,
      });
      setSelectedProject(defaultProjectId);
      setSelectedPhase();
      setSelectedCostCode();
      setSelectedLocal(defaultLocalId);
      setCustomFieldErrors({});
      setCurrentDivisionId(lockedDivisionId);
      setShowBreak(false);
      setShowOvertime(false);
      setShowDoubleOvertime(false);
      setShowPTO(false);
      setCurrentType('work');
      setCurrentDate(DateTime.local().toSQLDate());
      setIsMultiDayEntry(false);
    }
  }, [visible, form, defaultLocalId, lockedDivisionId, defaultProjectId]);

  useEffect(() => {
    if (!visible) return;
    if (id) setIsMultiDayEntry(false);
  }, [visible, id]);

  const showUnion = unions.length > 0
    && defaultClassId
    && (enableLocalSelection || enableClassSelection);

  const isApproved = selectedEntryState === 'approved';

  useEffect(() => {
    if (
      hasPTOPermission
      && isFutureDate
      && hourBased
    ) {
      setShowPTO(true);
    }
  }, [
    isFutureDate,
    hourBased,
    hasPTOPermission,
  ]);

  useEffect(() => {
    if (isFutureDate && !hourBased) {
      setHoursBased(true);
    }
  }, [isFutureDate, hourBased]);

  const baseDateComponent = useMemo(() => {
    const isPTO = !hasFutureDayPermission
      && isFutureDate
      && hasPTOPermission
      && hourBased
      && hasPaidFlag;
    return (
      <ManualEntryTimeHoursInput
        isMultiDayEntry={isMultiDayEntry}
        timezone={timezone}
        date={currentDate}
        hourBased={!!hourBased}
        start={{
          name: isPTO ? 'ptoStartTime' : 'startTime',
          label: isPTO ? 'PTO Start Time' : 'Start Time',
        }}
        end={{
          name: isPTO ? 'ptoEndTime' : 'endTime',
          label: isPTO ? 'PTO End Time' : 'End Time',
        }}
        hours={{
          name: isPTO ? 'ptoHours' : 'hours',
          label: isPTO ? 'PTO Hours' : 'Hours',
        }}
        rules={isPTO ? [
          {
            validator: ptoTimesValidator,
            required: true,
          },
        ] : []}
        required={!hasPaidFlag}
      />
    );
  }, [
    isFutureDate,
    hasPTOPermission,
    currentDate,
    hourBased,
    form,
    hasPaidFlag,
    timezone,
    isMultiDayEntry,
    hasFutureDayPermission,
  ]);

  const multiDayEntryComponent = useMemo(() => {
    // Do not show the multi-day entry checkbox if we are editing an existing entry
    if (id || !hasFutureDayPermission || !hourBased || isTimeEntryTable) return null;
    return (
      <Row>
        <OnTraccrCheckbox
          label="Multi-Day Entry"
          onChange={setIsMultiDayEntry}
          value={isMultiDayEntry}
          checkboxStyle={{
            marginRight: 0,
          }}
          textStyle={{
            fontSize: 13,
          }}
        />
      </Row>
    );
  }, [
    isMultiDayEntry,
    id,
    hasFutureDayPermission,
    hourBased,
    isTimeEntryTable,
  ]);

  useEffect(() => {
    if (visible) {
      const {
        userId: safeSelectedUserId,
      } = selectedEntry || {};
      const entryUsers = [];
      if (safeSelectedUserId) {
        entryUsers.push(safeSelectedUserId);
      } else if (user?.id) {
        entryUsers.push(user.id);
      } else if (userIds?.length) {
        entryUsers.push(...userIds);
      }
      setSelectedUserIds(entryUsers);
    }
  }, [visible, selectedEntry, user, userIds]);

  if (visible && !prevVisible) {
    const safeSelected = (selectedEntry || {});
    let ourDivisionId = safeSelected.divisionId || lockedDivisionId;
    if (!ourDivisionId && userDivs.length === 1) [ourDivisionId] = userDivs;
    if (!ourDivisionId && safeSelected.projectId) {
      ourDivisionId = projectMap[safeSelected.projectId]?.divisionId;
    }
    if (!ourDivisionId && safeSelected.costcodeId) {
      ourDivisionId = ccMap[safeSelected.costcodeId]?.divisionId;
    }

    const newData = {
      // Defaults for new entries (overridden by safeSelected when editing)
      projectId: defaultProjectId ?? null,
      phaseId: null,
      costcodeId: null,
      ...safeSelected,
      // Computed fields that always apply
      localId: defaultLocalId,
      classId: ourClassId,
      sageShiftId: sageShiftId || defaultSageShiftId,
      date: safeSelected.date || defaultDate,
      divisionId: ourDivisionId,
      startTime: TaskHelpers.getDisplayStart(selectedEntry, showOriginalTimes) || defaultStartTime,
      endTime: shiftedEndTime || defaultEndTime,
    };

    // OT-dependent display logic (total hours vs separate segments) is applied in the
    // useEffect above once the OT profile map has loaded, since map is empty on first render.

    let ourPhase = safeSelected.projectId
      && safeSelected.phaseId === null
      && safeSelected.costcodeId
      ? UNPHASED
      : safeSelected.phaseId;
    if (!ourPhase && safeSelected.projectId) {
      const relevantPhases = activePhases
        .filter((phase) => phase.projectId === safeSelected.projectId);
      if (relevantPhases.length === 0) {
        ourPhase = UNPHASED;
      }
    }
    newData.phaseId = ourPhase;

    // When using TreeSelect for costcodes, convert costcodeId to tree format
    const useTreeFormat = enableGlobalCCOnProject
      && safeSelected.projectId
      && isTimeEntryTable
      && safeSelected.costcodeId;
    if (useTreeFormat) {
      const costcode = ccMap[safeSelected.costcodeId];
      const isGlobalCostcode = costcode && !costcode.projectId;
      if (isGlobalCostcode) {
        newData.costcodeId = `global.${safeSelected.costcodeId}`;
      } else {
        const phaseForKey = ourPhase === UNPHASED ? null : ourPhase;
        newData.costcodeId = getPhaseCostcodeKey(phaseForKey, safeSelected.costcodeId);
      }
    }

    form.setFieldsValue(newData);
    setSelectedProject(safeSelected.projectId ?? defaultProjectId);
    setSelectedLocal(defaultLocalId);
    setSelectedCostCode(safeSelected.costcodeId);
    setSelectedPhase(ourPhase);
    setCurrentDivisionId(ourDivisionId);
    setCurrentType(safeSelected.type ?? 'work');
    setCurrentDate(selectedEntry?.date ?? defaultDate);
    if (!enableHourBasedTracking) {
      setHoursBased(false);
    } else if (selectedEntry) {
      setHoursBased(
        isNullOrUndefined(selectedEntry.hourBased) ? true : !!selectedEntry.hourBased,
      );
    } else {
      setHoursBased((!defaultStartTime && !defaultEndTime));
    }
  }

  if (visible !== prevVisible) {
    setPrevVisible(visible);
    if (!visible) setOtInitApplied(false);
  }

  const showExcludeFromAutoBreak = (
    (!selectedEntry || (isTimeEntryTable && !selectedEntry.isPreloaded))
    && autoBreakEnabled
    && Permissions.has('AUTO_BREAK_EXCLUDE')
  );

  return (
    <Drawer
      title="Add Time Entry"
      width={700}
      visible={visible}
      onClose={onClose}
      maskClosable={false}
      bodyStyle={{ marginBottom: FOOTER_HEIGHT, overflowY: 'auto' }}
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={onValuesChange}
        initialValues={{
          ...(selectedEntry || {}),
          projectId: selectedEntry?.projectId ?? defaultProjectId,
          localId: defaultLocalId,
          classId: ourClassId,
          sageShiftId: defaultSageShiftId,
          type,
          date: defaultDate,
          customData,
          divisionId: lockedDivisionId || userDivs.length === 1 ? userDivs[0] : null,
          endTime: shiftedEndTime ?? defaultEndTime,
          startTime: selectedEntry?.startTime ?? defaultStartTime,
          dateRange: [defaultDate, defaultDate],
          isNightShift: !!selectedEntryIsNightShift,
        }}
      >
        <Row>
          <Col span={isMultiDayEntry ? 12 : 8}>
            { !isMultiDayEntry && (
              <Form.Item
                name="date"
                label={(
                  <Row align="middle" gutter={8}>
                    <Col>
                      Date
                    </Col>
                    <Col>
                      {multiDayEntryComponent}
                    </Col>
                  </Row>
                )}
                rules={[{
                  validator: dateValidator({
                    form,
                    hourBased,
                    isOnlyPTO,
                    hasFutureDayPermission,
                  }),
                  required: true,
                }]}
                style={{ marginBottom: 5 }}
              >
                <ManualEntryDatePicker allowFuture={hasPaidFlag || hasFutureDayPermission} />
              </Form.Item>
            )}
            { isMultiDayEntry && (
              <Form.Item
                name="dateRange"
                label={(
                  <Row align="middle" gutter={12}>
                    <Col>
                      Date Range
                    </Col>
                    <Col>
                      {multiDayEntryComponent}
                    </Col>
                  </Row>
                )}
              >
                <ManualEntryDateRangePicker />
              </Form.Item>
            )}
          </Col>
          {baseDateComponent}
          {enableHourBasedTracking && !isFutureDate && (
            <Col span={4} style={{ display: 'flex', alignItems: 'center' }}>
              <Row align="bottom" gutter={8}>
                <Col>
                  <OnTraccrSwitch value={hourBased} onChange={onSwitchChange} />
                </Col>
                <Col>
                  {hourBased ? 'Hours' : 'Times'}
                </Col>
              </Row>
            </Col>
          )}
        </Row>
        <Row style={{ margin: '5px 0px' }}>
          <Col span={isMultiDayEntry ? 12 : 8} />
          {
            showBreak && (
              <ManualEntryTimeHoursInput
                date={currentDate}
                hourBased={!!hourBased}
                start={{
                  name: 'breakStartTime',
                  label: 'Break Start Time',
                }}
                end={{
                  name: 'breakEndTime',
                  label: 'Break End Time',
                }}
                hours={{
                  name: 'breakHours',
                  label: 'Break Hours',
                }}
                timezone={timezone}
              />
            )
          }
          {!isFutureDate && allowInlineTimes && (!id || hasPaidFlag) && currentType !== 'break' && (
            <Col span={4}>
              <BorderlessButton
                title={!showBreak ? 'Add Break' : 'Remove Break'}
                onClick={() => setShowBreak(!showBreak)}
                style={{ padding: 0, height: 20, textAlign: 'left' }}
              />
            </Col>
          )}
        </Row>
        <Row style={{ margin: '5px 0px' }}>
          <Col span={8} />
          {
            showOvertime && (
              <ManualEntryTimeHoursInput
                date={currentDate}
                hourBased={!!hourBased}
                start={{
                  name: 'otStartTime',
                  label: 'Overtime Start Time',
                }}
                end={{
                  name: 'otEndTime',
                  label: 'Overtime End Time',
                }}
                hours={{
                  name: 'otHours',
                  label: 'Overtime Hours',
                }}
                timezone={timezone}
              />
            )
          }
          {!isFutureDate && enableManualOT && allowInlineTimes && (!id || hasPaidFlag) && currentType !== 'overtime' && (
            <Col span={4}>
              <BorderlessButton
                title={!showOvertime ? 'Add Overtime' : 'Remove Overtime'}
                onClick={() => setShowOvertime(!showOvertime)}
                style={{ padding: 0, height: 20, textAlign: 'left' }}
              />
            </Col>
          )}
        </Row>
        {((!isFutureDate && enableManualOT && allowInlineTimes && hasPaidFlag) || showDoubleOvertime) && (
          <Row>
            <Col span={8} />
            {
              showDoubleOvertime
              && (
                <ManualEntryTimeHoursInput
                  date={currentDate}
                  hourBased={!!hourBased}
                  start={{
                    name: 'doubleOTStartTime',
                    label: '2X Overtime Start Time',
                  }}
                  end={{
                    name: 'doubleOTEndTime',
                    label: '2X Overtime End Time',
                  }}
                  hours={{
                    name: 'doubleOTHours',
                    label: '2X Overtime Hours',
                  }}
                  timezone={timezone}
                />
              )
            }
            {!isFutureDate && enableManualOT && allowInlineTimes && hasPaidFlag && (
              <Col span={4}>
                <BorderlessButton
                  title={!showDoubleOvertime ? 'Add 2X Overtime' : 'Remove 2X Overtime'}
                  onClick={() => setShowDoubleOvertime(!showDoubleOvertime)}
                  style={{ padding: 0, height: 20, textAlign: 'left' }}
                />
              </Col>
            )}
          </Row>
        )}
        {(
          showPTO
          || (hasPTOPermission && allowInlineTimes && (!id || hasPaidFlag) && currentType !== 'pto')
        ) && (!isFutureDate || hasFutureDayPermission) && !!hourBased && (
          <Row style={{ margin: '5px 0px' }}>
            <Col span={8} />
            {
              showPTO && (
                <ManualEntryTimeHoursInput
                  date={currentDate}
                  hourBased={!!hourBased}
                  start={{
                    name: 'ptoStartTime',
                    label: 'PTO Start Time',
                  }}
                  end={{
                    name: 'ptoEndTime',
                    label: 'PTO End Time',
                  }}
                  hours={{
                    name: 'ptoHours',
                    label: 'PTO Hours',
                  }}
                  disabled={!hasPTOPermission}
                  timezone={timezone}
                />
              )
            }
            {hasPTOPermission && allowInlineTimes && hasPaidFlag && currentType !== 'pto' && (
              <Col span={4}>
                <BorderlessButton
                  title={!showPTO ? 'Add PTO' : 'Remove PTO'}
                  onClick={() => setShowPTO(!showPTO)}
                  style={{ padding: 0, height: 20, textAlign: 'left' }}
                />
              </Col>
            )}
          </Row>
        )}
        <Row gutter={20}>
          { ((!hasPaidFlag && (!isFutureDate || hasFutureDayPermission)) || type !== 'work') && (
            <Col>
              <Form.Item
                name="type"
                label="Type"
                rules={[{ required: true, message: 'Type is required' }]}
              >
                <Select
                  defaultValue="work"
                  options={typeOptions}
                  placeholder="Select a Type"
                  style={{ width: 200 }}
                />
              </Form.Item>
            </Col>
          )}
          {
            relevantDivisions.length > 0
            && (
              <Col span={8}>
                <Form.Item
                  name="divisionId"
                  valuePropName="divisionId"
                  label="Division"
                  rules={[{ required: true, message: 'Division is required' }]}
                >
                  <DivisionSelector
                    disabled={!!lockedDivisionId}
                    filter={availableDivisionIds}
                  />
                </Form.Item>
              </Col>
            )
          }
        </Row>
        {showExcludeFromAutoBreak && (
          <Row gutter={20}>
            <Col>
              <Form.Item
                name="excludeFromAutoBreak"
              >
                <OnTraccrCheckbox label="Exclude from Auto Break Calculations" />
              </Form.Item>
            </Col>
          </Row>
        )}
        {enableNightShiftPremium && (
          <Row gutter={20}>
            <Col>
              <Form.Item
                name="isNightShift"
              >
                <OnTraccrCheckbox label="Night Shift Eligible" />
              </Form.Item>
            </Col>
          </Row>
        )}
        <Row>
          <Col span={24}>
            <Form.Item
              name="projectId"
              label={t('Project')}
              rules={[{ required: reqProject, message: `${t('Project')} is required` }]}
            >
              <Select
                options={projectOptions}
                placeholder={`Select a ${t('Project')}`}
                showSearch
                optionFilterProp="label"
                filterOption={filterSelectDropdown}
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
        <Row style={{ display: !!selectedProject && !onlyUnphased ? 'block' : 'none' }}>
          <Col span={24}>
            <Form.Item
              name="phaseId"
              label="Phase"
              rules={[{ required: !!selectedProject && !onlyUnphased && reqCostcode, message: 'Phase is required' }]}
            >
              <Select
                options={phaseOptions}
                placeholder="Select a Phase"
                showSearch
                optionFilterProp="label"
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              name="costcodeId"
              label="Cost Code"
              rules={[{ required: reqCostcode, message: 'Cost Code is required' }]}
            >
              {(enableGlobalCCOnProject && selectedProject && isTimeEntryTable) ? (
                <CostcodesTreeSelector
                  projectId={selectedProject}
                  phaseId={selectedPhase}
                  divisionId={currentDivisionId}
                  isClockIn
                  disabled={
                    (reqProject && !selectedProject)
                    || (!onlyUnphased && !selectedPhase)
                  }
                />
              ) : (
                <Select
                  options={costcodeOptions}
                  placeholder="Select a Cost Code"
                  showSearch
                  optionFilterProp="label"
                  allowClear
                  disabled={reqProject && (!selectedProject || !selectedPhase)}
                />
              )}
            </Form.Item>
          </Col>
        </Row>
        {
          (showUnion || enableSageShiftSelection)
          && (
          <Row gutter={20}>
            {enableLocalSelection && (
            <Col span={8}>
              <Form.Item
                name="localId"
                label="Union Local"
              >
                <Select
                  options={relevantLocals}
                  placeholder="Select a Local"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            )}
            {enableClassSelection && (
            <Col span={8}>
              <Form.Item
                name="classId"
                label="Work Classification"
              >
                <Select
                  options={relevantClasses}
                  placeholder="Select a Classification"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            )}
            {enableSageShiftSelection && (
            <Col span={8}>
              <Form.Item
                name="sageShiftId"
                label="Shift"
              >
                <Select
                  options={sageShiftArray}
                  placeholder="Select a Shift"
                  showSearch
                  optionFilterProp="label"
                  allowClear
                />
              </Form.Item>
            </Col>
            )}
          </Row>
          )
        }
        {!hideUserSelector && (
          <Row>
            <Col span={24}>
              <Form.Item label="Users" required>
                <Select
                  value={selectedUserIds}
                  onChange={setSelectedUserIds}
                  options={userOptions}
                  placeholder="Select Users"
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  mode="multiple"
                  disabled={isApproved}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
        <FormTextInput
          isNotDisplay
          label="Note"
          name="note"
          textarea
        />
        {
          ourCustomFields && ourCustomFields.length > 0
          && !isOnlyPTO
          && (
            <Form.Item
              name="customData"
              label="Custom Fields"
              style={{ marginTop: 10 }}
            >
              <ManualEntryCustomFields
                key={`${id}${visible}`} // Need to reset component for initialValues to load
                errors={customFieldErrors}
                initialValues={customData}
                fileMap={fileMap}
                divisionId={currentDivisionId}
                timeData={timeData}
                project={selectedProject}
                costcode={selectedCostCode}
                timecardPermissionMap={timecardPermissionMap}
              />
            </Form.Item>
          )
        }
      </Form>
      <DrawerSubmitFooter
        onClose={onClose}
        onSubmit={onSubmitClicked}
        submitTitle="Save"
        actionTitle="Redistribute Hours"
      />

    </Drawer>
  );
}

/* eslint-disable react/forbid-prop-types */
ManualEntryFormDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onManualProjectIdChange: PropTypes.func,
  selectedEntry: PropTypes.shape({
    id: PropTypes.string.isRequired,
    date: PropTypes.string,
    startTime: PropTypes.number,
    endTime: PropTypes.number,
    breakStartTime: PropTypes.number,
    breakEndTime: PropTypes.number,
    otStartTime: PropTypes.number,
    otEndTime: PropTypes.number,
    doubleOTStartTime: PropTypes.number,
    doubleOTEndTime: PropTypes.number,
    ptoStartTime: PropTypes.number,
    ptoEndTime: PropTypes.number,
    isPreloaded: PropTypes.bool,
  }),
  defaultSageShiftId: PropTypes.string,
  defaultClassId: PropTypes.string,
  defaultProjectId: PropTypes.string,
  defaultDate: PropTypes.string,
  defaultStartTime: PropTypes.number,
  defaultEndTime: PropTypes.number,
  fileMap: PropTypes.object.isRequired,
  userId: PropTypes.string.isRequired,
  lockedDivisionId: PropTypes.string,
  divisionIds: PropTypes.arrayOf(PropTypes.string),
  entries: PropTypes.array,
  user: PropTypes.object,
  userIds: PropTypes.arrayOf(PropTypes.string),
  allowInlineTimes: PropTypes.bool,
  hideUserSelector: PropTypes.bool,
  shouldRoundTimes: PropTypes.bool,
  isTimeEntryTable: PropTypes.bool,
};

ManualEntryFormDrawer.defaultProps = {
  onManualProjectIdChange: null,
  selectedEntry: null,
  defaultSageShiftId: null,
  defaultClassId: null,
  defaultProjectId: null,
  defaultDate: DateTime.local().toSQLDate(),
  defaultStartTime: null,
  defaultEndTime: null,
  lockedDivisionId: null,
  divisionIds: [],
  entries: [],
  user: {},
  userIds: [],
  allowInlineTimes: false,
  hideUserSelector: false,
  shouldRoundTimes: false,
  isTimeEntryTable: false,
};

export default ManualEntryFormDrawer;
